/**
 * HistoryMenu — menu déroulant « Historique » de la barre d'outils.
 *
 * Remplace le bloc Historique de la colonne de gauche (place rendue à la liste
 * des calques). Tête de menu : Annuler / Rétablir ; puis la liste des états
 * (plus récent en haut), clic sur un état = y revenir. Le menu reste ouvert au
 * clic sur un état pour naviguer rapidement entre plusieurs états.
 */
import { useState, useRef, useEffect } from 'react';
import { useLabelStore } from '../store/useLabelStore';

export default function HistoryMenu() {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const history = useLabelStore(s => s.history);
    const historyIndex = useLabelStore(s => s.historyIndex);
    const undo = useLabelStore(s => s.undo);
    const redo = useLabelStore(s => s.redo);
    const goToHistory = useLabelStore(s => s.goToHistory);

    useEffect(() => {
        if (!open) return;
        const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen((o) => !o)}
                title="Historique des actions (annuler / rétablir / revenir à un état)"
                className={`flex items-center gap-2 pl-3 pr-2.5 py-1.5 rounded-xl border transition-all ${
                    open
                        ? 'bg-white/10 border-white/15 text-white'
                        : 'bg-black/20 border-white/5 text-white/60 hover:text-white hover:bg-white/10'
                }`}
            >
                {/* Icône horloge-retour */}
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                    <polyline points="3 3 3 8 8 8" />
                    <polyline points="12 7 12 12 16 14" />
                </svg>
                {/* lf-menu-label : cf. la container query dans globals.css */}
                <span className="lf-menu-label text-[10px] font-bold uppercase tracking-widest pt-0.5">Historique</span>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"
                     className={`transition-transform ${open ? 'rotate-180' : ''}`}>
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>

            {open && (
                <div className="absolute left-0 top-full mt-2 w-72 bg-forge-bg border border-white/10 rounded-xl shadow-2xl z-50 py-2 overflow-hidden">
                    {/* Tête : annuler / rétablir */}
                    <div className="flex items-center justify-between px-3 pb-2 border-b border-white/5">
                        <span className="text-[8px] font-mono uppercase tracking-[0.2em] text-white/30">
                            Historique ({history.length})
                        </span>
                        <div className="flex gap-1.5">
                            <button
                                onClick={undo}
                                disabled={historyIndex <= 0}
                                className="px-3 py-1 text-[11px] font-mono bg-black/40 border border-white/5 rounded-lg disabled:opacity-20 hover:bg-white/10 text-white/70 hover:text-white transition-all"
                                title="Annuler (Ctrl+Z)" aria-label="Annuler (Ctrl+Z)"
                            >↶</button>
                            <button
                                onClick={redo}
                                disabled={historyIndex >= history.length - 1}
                                className="px-3 py-1 text-[11px] font-mono bg-black/40 border border-white/5 rounded-lg disabled:opacity-20 hover:bg-white/10 text-white/70 hover:text-white transition-all"
                                title="Refaire (Ctrl+Shift+Z)" aria-label="Refaire (Ctrl+Shift+Z)"
                            >↷</button>
                        </div>
                    </div>
                    {/* Liste des états — plus récent en haut, clic = y revenir */}
                    <div className="max-h-80 overflow-y-auto custom-scrollbar p-1.5 space-y-1">
                        {history.length === 0 && (
                            <div className="text-center text-white/40 text-xs p-4 italic opacity-50">Aucun historique.</div>
                        )}
                        {[...history].reverse().map((snap, ri) => {
                            const actualIndex = history.length - 1 - ri;
                            const isCurrent = actualIndex === historyIndex;
                            const isFuture = actualIndex > historyIndex;
                            return (
                                <div
                                    key={actualIndex}
                                    onClick={() => goToHistory(actualIndex)}
                                    className={`flex items-center justify-between rounded-lg px-3 py-1.5 cursor-pointer transition-all duration-150 ${
                                        isCurrent
                                            ? 'bg-forge-accent/10 border border-white/30 text-white shadow-[0_0_10px_rgba(var(--forge-accent-rgb),0.1)]'
                                            : isFuture
                                                ? 'opacity-30 hover:opacity-100 hover:bg-white/5 border border-transparent text-white/40'
                                                : 'bg-black/30 border border-transparent hover:bg-white/5 hover:border-white/10'
                                    }`}
                                >
                                    <span className={`text-[9px] font-mono tracking-wider uppercase ${isCurrent ? 'font-bold' : ''}`}>
                                        {actualIndex} : {snap.actionName || 'Action'}
                                    </span>
                                    {isCurrent && (
                                        <span className="text-[8px] bg-white text-black px-1.5 py-0.5 rounded shadow-inner font-bold tracking-widest uppercase">Actuel</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
